// Components
import GradientBarGraph from "@/DataPages/components/FniManagement/components/CIT/GradientBarGraph/GradientBarGraph";

// MUI
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { useMyCitContextData } from "@/DataPages/context/myCit/MyCitContext";
import { IGetUsersResponse } from "@/Common/api/users/queries";
import { useMemo } from "react";
import { generateAnalyticsData } from "@DataPages/helpers/financeManagement/cit/analytics";

interface IMyCitAnalyticsProps {
	isLoading: boolean;
	title?: string;
	financeManagers?: IGetUsersResponse[];
}

export default function MyCitAnalytics(props: Readonly<IMyCitAnalyticsProps>) {
	const { financeManagers = [] } = props;
	const { deals, filteredDeals, citSettings } = useMyCitContextData();

	const usedDeals = useMemo(
		() => deals.filter((deal) => (!filteredDeals || filteredDeals.includes(deal._id)) && !deal.funded),
		[deals, filteredDeals]
	);

	const data = useMemo(() => {
		if (!citSettings) {
			return [];
		}
		const citGraphWidth = parseInt((citSettings.citGraphWidth ?? "").replace(" Days", ""), 10) ?? 0;
		return generateAnalyticsData({ deals: usedDeals, graphWidth: citGraphWidth });
	}, [usedDeals, citSettings]);

	const maxYDollars = (citSettings?.citMaxGraphValues.maxUnfundedDollars ?? 0) / (financeManagers.length || 1);
	const maxYContracts = (citSettings?.citMaxGraphValues.maxUnfundedContracts ?? 0) / (financeManagers.length || 1);

	return (
		<Grid container alignContent={"flex-start"} className="cit-analytics">
			<Grid item xs={12}>
				{props.isLoading ? (
					<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
				) : (
					<>
						{props?.title && (
							<Typography className={"page-title"}>{`${props?.title} Contracts In Transit Analytics`}</Typography>
						)}
					</>
				)}
			</Grid>
			<Grid item xs={12} sx={{ marginTop: "30px" }}>
				<Grid container direction="row">
					<Grid item xs={6} sx={{ paddingLeft: "40px" }}>
						<GradientBarGraph
							title="Unfunded Dollars"
							data={data}
							xAxis="age"
							dataKey="amount"
							maxY={maxYDollars}
							decimals={maxYDollars > 1 ? 0 : 2}
							symbol="$"
							postfix="M"
							isLoading={props.isLoading}
							yAxisLabel="Amounts ($)"
							xAxisLabel="Transit (days)"
						/>
					</Grid>
					<Grid item xs={6}>
						<GradientBarGraph
							title="Unfunded Contracts"
							data={data}
							xAxis="age"
							dataKey="quantity"
							maxY={maxYContracts}
							isLoading={props.isLoading}
							yAxisLabel="# of Contracts"
							xAxisLabel="Transit (days)"
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item container direction="column" alignItems="center">
				<Grid item>
					<Grid container direction="column" className="mt-[1.5rem]">
						{props.isLoading ? (
							<Skeleton variant="rectangular" className="rounded-md !h-3 w-[525px]"></Skeleton>
						) : (
							<>
								<Grid item>
									<Typography className="!font-montserrat !text-[8pt] !font-semibold text-default pl-[5px]">
										CIT Severity
									</Typography>
								</Grid>
								<Grid item>
									<Box className="rounded-md h-3 w-[525px] bg-gradient-to-r from-primary to-high-severity" />
								</Grid>
								<Grid item>
									<Grid container direction="row" justifyContent="space-between" className="w-full">
										<Grid item>
											<Typography className="!font-montserrat !text-[8pt] !font-semibold text-default pl-[5px]">
												Low
											</Typography>
										</Grid>
										<Grid item>
											<Typography className="!font-montserrat !text-[8pt] !font-semibold text-default">
												High
											</Typography>
										</Grid>
									</Grid>
								</Grid>
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
